import React, { useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, RotateCcw, Ruler } from 'lucide-react';

interface SizeObject {
  id: string;
  name: string;
  size: 'small' | 'medium' | 'large';
  length: number;
  color: string;
  emoji: string;
}

interface SizeComparisonResource {
  type: 'size_comparison';
  name: string;
  title: string;
  description: string;
  objects: SizeObject[];
  instructions: string;
  showMeasurements: boolean;
  allowDrag: boolean;
}

interface SizeComparisonComponentProps {
  resource: SizeComparisonResource;
  onAction?: (action: string, data?: any) => void;
  isPreview?: boolean;
}

const SizeComparisonComponent: React.FC<SizeComparisonComponentProps> = ({
  resource,
  onAction,
  isPreview = false
}) => {
  const [sortedObjects, setSortedObjects] = useState<SizeObject[]>([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Initialiser avec les objets dans l'ordre aléatoire
  useEffect(() => {
    const shuffled = [...resource.objects].sort(() => Math.random() - 0.5);
    setSortedObjects(shuffled);
  }, [resource.objects]);

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
    setShowFeedback(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setHoveredIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setHoveredIndex(null);
      return;
    }

    const newObjects = Array.from(sortedObjects);
    const [movedObject] = newObjects.splice(draggedIndex, 1);
    newObjects.splice(dropIndex, 0, movedObject);
    
    setSortedObjects(newObjects);
    setDraggedIndex(null);
    setHoveredIndex(null);
  }, [draggedIndex, sortedObjects]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setHoveredIndex(null);
  }, []);

  const validateOrder = useCallback(() => {
    const correctOrder = [...resource.objects].sort((a, b) => a.length - b.length);
    const isCorrect = sortedObjects.every((obj, index) => 
      obj.id === correctOrder[index].id
    );

    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);
    setShowFeedback(true);
    setIsCompleted(isCorrect);

    if (onAction) {
      onAction('validation', {
        isCorrect,
        attempts: nextAttempts,
        sortedObjects: sortedObjects.map(obj => ({ id: obj.id, name: obj.name, length: obj.length }))
      });
    }

    return isCorrect;
  }, [sortedObjects, resource.objects, attempts, onAction]);

  const resetOrder = useCallback(() => {
    const shuffled = [...resource.objects].sort(() => Math.random() - 0.5);
    setSortedObjects(shuffled);
    setIsCompleted(false);
    setShowFeedback(false);
    setAttempts(0);
    setDraggedIndex(null);
    setHoveredIndex(null);
  }, [resource.objects]);

  const getSizeColor = (size: string) => {
    switch (size) {
      case 'small': return 'bg-green-100 border-green-300 text-green-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'large': return 'bg-red-100 border-red-300 text-red-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };


  return (
    <div className="size-comparison-component p-6 bg-white rounded-lg shadow-lg">
      {/* En-tête */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Ruler className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-bold text-gray-800">{resource.title}</h3>
        </div>
        <p className="text-gray-600 mb-4">{resource.description}</p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 font-medium">{resource.instructions}</p>
        </div>
      </div>

      {/* Zone de tri */}
      <div className="mb-6">
        <div className="min-h-[120px] p-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
          <div className="text-sm text-gray-600 mb-4 text-center">
            Glissez les icônes pour les ranger du plus petit au plus grand (de gauche à droite)
          </div>
          <div className="flex flex-wrap gap-6 justify-center">
            {sortedObjects.map((obj, index) => (
              <div
                key={obj.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all duration-200 cursor-move relative min-w-[100px] ${
                  draggedIndex === index 
                    ? 'opacity-50 scale-95' 
                    : hoveredIndex === index 
                    ? 'scale-105 shadow-lg' 
                    : 'hover:shadow-md'
                } ${getSizeColor(obj.size)}`}
              >
                {/* Indicateur de position */}
                <div className="absolute -top-2 -left-2 w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                
                {/* Icône redimensionnée */}
                <div 
                  className="mb-2 transition-all duration-200"
                  style={{
                    fontSize: `${Math.max(32, Math.min(64, 32 + obj.length * 1.5))}px`,
                  }}
                >
                  {obj.emoji}
                </div>
                
                {/* Mesures (optionnel) */}
                {resource.showMeasurements && (
                  <div className="text-xs opacity-75 font-medium">{obj.length} cm</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Feedback */}
      {showFeedback && (
        <div className={`mb-4 p-4 rounded-lg border-2 ${
          isCompleted 
            ? 'bg-green-50 border-green-300 text-green-800' 
            : 'bg-red-50 border-red-300 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="font-medium">
              {isCompleted 
                ? 'Excellent ! Les objets sont dans le bon ordre.' 
                : 'Pas tout à fait. Essayez de réorganiser les objets du plus petit au plus grand.'
              }
            </span>
          </div>
          {!isCompleted && (
            <div className="mt-2 text-sm">
              <p className="font-medium mb-2">Ordre correct :</p>
              <div className="flex flex-wrap gap-4 justify-center">
                {resource.objects
                  .sort((a, b) => a.length - b.length)
                  .map((obj, index) => (
                    <div key={obj.id} className="flex flex-col items-center gap-1 bg-gray-100 px-4 py-3 rounded-lg">
                      <span className="text-xs font-bold text-blue-600">{index + 1}.</span>
                      <div 
                        style={{
                          fontSize: `${Math.max(24, Math.min(40, 24 + obj.length * 1.0))}px`,
                        }}
                      >
                        {obj.emoji}
                      </div>
                      {resource.showMeasurements && (
                        <span className="text-xs text-gray-500 font-medium">{obj.length}cm</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contrôles */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={validateOrder}
          disabled={isCompleted}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            isCompleted
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isCompleted ? '✓ Terminé' : 'Valider l\'ordre'}
        </button>
        
        <button
          onClick={resetOrder}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Recommencer
        </button>
      </div>

      {/* Statistiques */}
      <div className="mt-4 text-center text-sm text-gray-600">
        <p>Tentatives : {attempts}</p>
        {isCompleted && (
          <p className="text-green-600 font-medium">
            Félicitations ! Vous avez réussi en {attempts} tentative{attempts > 1 ? 's' : ''}.
          </p>
        )}
      </div>
    </div>
  );
};

export { SizeComparisonComponent };
export default SizeComparisonComponent;
